import type { ComponentType } from 'react';
import { Trans } from 'react-i18next';
import { Alert, AlertDescription } from '@parrot-software/design-system';
import { CircleAlert, TriangleAlert } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';
import type { SubscriptionEventViewDto } from '@subscriptions/application/dtos';
import { SubscriptionEvents } from '@subscriptions/domain/types';

interface SubscriptionEventBannerProps {
  /** Event data driving the banner copy and interpolation variables. */
  event: SubscriptionEventViewDto;
}

/** Visual tone of a banner — decoupled from the event so copy and treatment vary independently. */
type BannerTone = 'destructive' | 'warning';

/** Visual treatment per banner tone: Alert variant/classes and icon. */
type TonePresentation = {
  variant?: 'destructive';
  className: string;
  Icon: ComponentType<{ className?: string }>;
};

const sharedLayoutClasses =
  'flex items-start gap-3 rounded-lg font-sans [&>svg]:relative [&>svg]:left-auto [&>svg]:top-auto [&>svg+div]:translate-y-0 [&>svg~*]:pl-0';

/**
 * Visual treatment per tone. `destructive` maps to the Alert variant of the same
 * name (red, circle-alert); the design system `Alert` has no warning variant, so
 * `warning` applies the semantic warning tokens via classes (`bg-bg-warning` +
 * `border-warning`/`text-warning` — same idiom as `PaymentStatusBadge`).
 */
const TONE_PRESENTATIONS: Record<BannerTone, TonePresentation> = {
  destructive: { variant: 'destructive', className: 'bg-destructive/10', Icon: CircleAlert },
  warning: {
    className: 'bg-bg-warning border-warning text-warning [&>svg]:text-warning',
    Icon: TriangleAlert,
  },
};

/**
 * Copy key + tone per event type: the four collection events are destructive, WARNING is yellow.
 *
 * The two events that name the point of sale block date carry a second key, used when the API
 * sends no such date, so the copy drops the sentence instead of printing an empty one.
 */
const EVENT_PRESENTATIONS: Record<
  SubscriptionEventViewDto['event'],
  { i18nKey: string; noPosDateI18nKey?: string; tone: BannerTone }
> = {
  [SubscriptionEvents.FEE]: { i18nKey: 'subscriptions:banner.events.fee', tone: 'destructive' },
  [SubscriptionEvents.PRE_FEE]: {
    i18nKey: 'subscriptions:banner.events.preFee',
    noPosDateI18nKey: 'subscriptions:banner.events.preFeeNoPosDate',
    tone: 'destructive',
  },
  [SubscriptionEvents.URGENT]: {
    i18nKey: 'subscriptions:banner.events.urgent',
    noPosDateI18nKey: 'subscriptions:banner.events.urgentNoPosDate',
    tone: 'destructive',
  },
  [SubscriptionEvents.PREVENTIVE]: { i18nKey: 'subscriptions:banner.events.preventive', tone: 'destructive' },
  [SubscriptionEvents.WARNING]: { i18nKey: 'subscriptions:banner.events.warning', tone: 'warning' },
};

/** Maps the `<strong>` tag in the i18n copy to a semibold span. */
const strongComponents = { strong: <strong className="font-bold" /> };

/**
 * SubscriptionEventBanner - Alert rendered for a single collection-status event
 * (`GET /v1/events`). The four collection variants share the destructive visual
 * treatment (red, circle-alert icon) per Figma, while the WARNING variant uses
 * the yellow warning treatment (triangle icon); only the copy differs otherwise.
 * The copy is pluralized natively by i18next via the `count` variable
 * (subscriptions count), with `date`, `posDate` and `day` interpolated where
 * they apply. Bold spans come from `<strong>` tags in the i18n strings and line
 * breaks from `\n` (rendered via `whitespace-pre-line`).
 */
export const SubscriptionEventBanner = ({ event }: SubscriptionEventBannerProps) => {
  const { i18nKey, noPosDateI18nKey, tone } = EVENT_PRESENTATIONS[event.event];
  const { Icon, className, variant } = TONE_PRESENTATIONS[tone];
  const copyKey = !event.posCutoffDate && noPosDateI18nKey ? noPosDateI18nKey : i18nKey;

  return (
    <Alert className={cn(sharedLayoutClasses, className)} variant={variant}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <AlertDescription className="whitespace-pre-line text-sm font-medium">
        <Trans
          components={strongComponents}
          i18nKey={copyKey}
          values={{
            count: event.subscriptionsCount,
            date: event.paymentDateCutoff,
            posDate: event.posCutoffDate,
            day: event.paymentDay ?? '',
          }}
        />
      </AlertDescription>
    </Alert>
  );
};
